<template>
  <sfs-form-modal
    :id="modalId"
    :title="title"
    @on-hide="handleHide"
  >
    <b-form
      novalidate
      @submit.stop.prevent="handleSubmit"
    >
      <b-row v-if="hasBaseServerErrors()">
        <b-col>
          <b-alert
            :show="hasBaseServerErrors()"
            variant="danger"
            dismissible
          >
            <p
              class="mb-0"
              v-for="(error, index) in baseServerErrors"
              :key="index"
            >
              {{ error }}
            </p>
          </b-alert>
        </b-col>
      </b-row>

      <b-row
        v-for="(emailValidation, index) in $v.form.emails.$each.$iter"
        :key="index"
        class="mb-3"
      >
        <b-col cols="11">
          <b-form-input
            class="email-form-modal-email-input"
            name="email"
            v-model.trim="emailValidation.email.$model"
            :state="emailValidation.email.$dirty ? !emailValidation.email.$error : null"
            autocomplete="new-password"
            type="text"
            placeholder="example@email.com"
          />
          <small
            v-if="index === 0 && helpText"
            class="form-text text-muted"
          >
            {{ helpText }}
          </small>
          <b-form-invalid-feedback
            v-if="emailValidation.email.$dirty && emailValidation.email.$error"
            class="email-form-modal-email-feedback"
            :state="false"
          >
            <span v-if="!emailValidation.email.required">Please enter an email.</span>
            <span v-else-if="!emailValidation.email.email">Please enter a valid email.</span>
            <span v-else-if="emailValidation.email.serverFailed === false">{{ serverErrors.emails || serverErrors.email }}</span>
          </b-form-invalid-feedback>
        </b-col>
        <b-col
          cols="1"
          class="pl-0"
        >
          <b-button
            class="email-form-modal-remove-email-button"
            @click="removeEmail(index)"
            :disabled="index === 0"
            variant="link"
          >
            <i class="far fa-trash-can" />
          </b-button>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col>
          <b-button
            class="email-form-modal-add-email-button"
            variant="outline-secondary"
            @click="addEmail"
          >
            <span> <i class="fa fa-plus" /> Add Email</span>
          </b-button>
        </b-col>
      </b-row>

      <slot
        name="fields"
        :form="form"
        :processing="processing"
      />

      <hr class="mt-4">

      <b-row>
        <b-col
          offset-lg="6"
          lg="3"
          order="2"
          order-lg="1"
        >
          <b-button
            variant="link-dark"
            block
            class="email-form-modal-cancel-button"
            @click="closeModal"
          >
            Cancel
          </b-button>
        </b-col>
        <b-col
          lg="3"
          order="1"
          order-lg="2"
        >
          <b-button
            class="email-form-modal-submit-button"
            block
            type="submit"
            variant="primary"
            :disabled="processing"
          >
            Email
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </sfs-form-modal>
</template>

<script>
import { required, email, minLength } from 'vuelidate/lib/validators';
import { isNil as _isNil } from 'lodash';
import formMixin from '@/mixins/formMixin';

export default {
  name: 'EmailFormModal',
  mixins: [formMixin],
  props: {
    modalId: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    helpText: String,
    initialEmails: {
      type: Array,
      default: null,
    },
    initialForm: {
      type: Object,
      default: () => ({}),
    },
    /**
     * When true, every email row may be left blank (e.g. recipients are resolved elsewhere).
     * Submit payload may still include an empty emails array.
     */
    allowEmptyEmailList: {
      type: Boolean,
      default: false,
    },
    request: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      form: this.buildInitialFormState(),
      fieldLabels: {
        emails: 'Email',
      },
    };
  },
  validations() {
    const optionalIfEmpty = (value) => {
      if (!this.allowEmptyEmailList) {
        return false;
      }
      const s = typeof value === 'string' ? value.trim() : value;
      return !s || s.length === 0;
    };

    return {
      form: {
        emails: {
          required: this.allowEmptyEmailList ? () => true : required,
          minLength: minLength(this.allowEmptyEmailList ? 0 : 1),
          $each: {
            email: {
              required(value) {
                if (optionalIfEmpty(value)) {
                  return true;
                }
                return required(value);
              },
              email(value) {
                if (optionalIfEmpty(value)) {
                  return true;
                }
                return email(value);
              },
              serverFailed: () => !this.hasServerErrors('emails') && !this.hasServerErrors('email'),
            },
          },
        },
      },
    };
  },
  watch: {
    initialEmails(newValue) {
      if (this.processing || this.$v?.form?.emails?.$anyDirty) {
        return;
      }

      if (!Array.isArray(newValue)) {
        return;
      }

      this.form.emails = newValue.map((value) => (typeof value === 'string' ? { email: value } : value));
    },
  },
  methods: {
    buildInitialFormState() {
      const base = { ...this.initialForm };
      const fallbackEmail = this.$store?.getters?.user?.email;

      return {
        ...base,
        emails: this.normalizeInitialEmails(this.initialEmails, fallbackEmail),
      };
    },
    resetFormState() {
      this.form = this.buildInitialFormState();
      this.resetServerErrors();
      this.$v.$reset();
      this.processing = false;
    },
    normalizeInitialEmails(initialEmails, fallbackEmail) {
      const normalizedInitialEmails = Array.isArray(initialEmails) ? initialEmails : null;
      const emails = normalizedInitialEmails
        ? normalizedInitialEmails.map((value) => (typeof value === 'string' ? { email: value } : value))
        : [{ email: fallbackEmail }];

      return emails.length ? emails : [{ email: null }];
    },
    closeModal() {
      this.$bvModal.hide(this.modalId);
      this.resetFormState();
    },
    handleHide() {
      this.resetFormState();
      this.$emit('on-hide');
    },
    addEmail() {
      this.form.emails.push({ email: null });
    },
    removeEmail(index) {
      this.form.emails.splice(index, 1);
    },
    buildPayload() {
      const payload = {};

      const normalizedEmails = this.normalizeInitialEmails(this.form.emails, null);
      const emails = normalizedEmails
        .map((item) => item && typeof item.email === 'string' ? item.email.trim() : item?.email)
        .filter((value) => !_isNil(value) && value !== '');
      payload.emails = emails;

      Object.keys(this.form || {}).forEach((key) => {
        if (key === 'emails') {
          return;
        }
        payload[key] = this.form[key];
      });

      return payload;
    },
    handleSubmit() {
      if (this.hasFormErrors()) {
        return;
      }

      this.processing = true;
      const payload = this.buildPayload();

      this.request(payload)
        .then((response) => {
          this.processing = false;
          this.resetServerErrors();
          this.$v.$reset();
          this.$emit('success', response);
          this.closeModal();
        })
        .catch((error) => {
          this.processing = false;
          const errors = error && error.response && error.response.data ? error.response.data.errors : null;
          this.processServerErrors(errors);
        });
    },
  },
};
</script>
